import type { ThemeVersionData } from "@klarity/themes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "../../../components/ui/Toast";
import { ApiError } from "../../../lib/api/client";
import { api } from "../../../lib/api/endpoints";
import type { FontInfo } from "../../../lib/api/models";
import { qk } from "../../../lib/queries";

export function useThemeDraft(themeId: string) {
  const queryClient = useQueryClient();
  // POST …/draft returns the existing draft or a fresh copy of the latest published version (plan 03).
  const draftQ = useQuery({
    queryKey: ["themeDraft", themeId],
    queryFn: () => api.themes.draft(themeId),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [data, setData] = useState<ThemeVersionData | null>(null);
  const [fonts, setFonts] = useState<FontInfo[]>([]);
  const [dirty, setDirty] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!draftQ.data) return;
    setData(draftQ.data.data);
    setFonts(draftQ.data.fonts);
    setDirty(false);
  }, [draftQ.data]);

  const update = (next: ThemeVersionData | ((current: ThemeVersionData) => ThemeVersionData)) => {
    setData((cur) => (typeof next === "function" ? (cur ? next(cur) : cur) : next));
    setDirty(true);
  };
  const addFont = (font: FontInfo) => setFonts((list) => [...list.filter((f) => f.src !== font.src), font]);

  const save = async (): Promise<boolean> => {
    if (!data) return false;
    try {
      const v = await api.themes.saveDraft(themeId, data);
      setFonts(v.fonts);
      setDirty(false);
      setServerErrors([]);
      void queryClient.invalidateQueries({ queryKey: qk.themes });
      return true;
    } catch (e) {
      if (e instanceof ApiError && Array.isArray(e.details.errors)) {
        setServerErrors(e.details.errors.filter((x): x is string => typeof x === "string"));
      } else toast(`The draft could not be saved: ${e instanceof Error ? e.message : "unknown error"}`, "error");
      return false;
    }
  };
  const discard = async () => {
    await api.themes.discardDraft(themeId);
    const r = await draftQ.refetch();
    if (r.data) {
      setData(r.data.data);
      setFonts(r.data.fonts);
      setDirty(false);
      setServerErrors([]);
    }
  };

  return {
    draft: draftQ.data,
    data,
    fonts,
    dirty,
    serverErrors,
    update,
    addFont,
    save,
    discard,
    isLoading: draftQ.isLoading,
  };
}
